import React from 'react';
import styled from 'styled-components';

import { useAdminGetDashboardReservationsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardReservationsDataQuery';
import { icons } from '../../themes/icons';
import { calculatePercentageChange } from '../../utils/dashboard';
import { toLocaleString } from '../../utils/format-number';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const ReservationCategory: React.FC<Props> = ({
  fromDate,
  toDate,
  priorFrom,
  priorTo,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching } = useAdminGetDashboardReservationsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID,
  });

  const {
    foodPercentage,
    drinkPercentage,
    fitnessPercentage,
    beautyPercentage,
    experiencePercentage,
    boutiquePercentage,
    eventPercentage,
  } = React.useMemo(() => {
    const foodPercentage = calculatePercentageChange(
      data?.totalFoodReservationsCurrent || 0,
      data?.totalFoodReservationsPrior || 0,
    );

    const drinkPercentage = calculatePercentageChange(
      data?.totalDrinkReservationsCurrent || 0,
      data?.totalDrinkReservationsPrior || 0,
    );

    const fitnessPercentage = calculatePercentageChange(
      data?.totalFitnessReservationsCurrent || 0,
      data?.totalFitnessReservationsPrior || 0,
    );

    const beautyPercentage = calculatePercentageChange(
      data?.totalBeautyReservationsCurrent || 0,
      data?.totalBeautyReservationsPrior || 0,
    );

    const experiencePercentage = calculatePercentageChange(
      data?.totalExperienceReservationsCurrent || 0,
      data?.totalExperienceReservationsPrior || 0,
    );

    const boutiquePercentage = calculatePercentageChange(
      data?.totalBoutiqueReservationsCurrent || 0,
      data?.totalBoutiqueReservationsPrior || 0,
    );

    const eventPercentage = calculatePercentageChange(
      data?.totalEventReservationsCurrent || 0,
      data?.totalEventReservationsPrior || 0,
    );

    return {
      foodPercentage,
      drinkPercentage,
      fitnessPercentage,
      beautyPercentage,
      experiencePercentage,
      boutiquePercentage,
      eventPercentage,
    };
  }, [data]);

  return (
    <BoxContainer>
      <AllStats>
        <Heading>Reservations</Heading>
        <Time>{selectedLabel}</Time>
      </AllStats>

      {data ? (
        <>
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Food"
            count={toLocaleString(data?.totalFoodReservationsCurrent || 0)}
            compare={`${foodPercentage}%`}
            icon={<icons.FoodIcon />}
            percentageNumber={foodPercentage}
          />

          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Drink"
            count={toLocaleString(data?.totalDrinkReservationsCurrent || 0)}
            compare={`${drinkPercentage}%`}
            icon={<icons.DrinkIcon />}
            percentageNumber={drinkPercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Fitness"
            count={toLocaleString(data?.totalFitnessReservationsCurrent || 0)}
            compare={`${fitnessPercentage}%`}
            icon={<icons.FitnessIcon />}
            percentageNumber={fitnessPercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Beauty"
            count={toLocaleString(data?.totalBeautyReservationsCurrent || 0)}
            compare={`${beautyPercentage}%`}
            icon={<icons.BeautyIcon />}
            percentageNumber={beautyPercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Experience"
            count={toLocaleString(data?.totalExperienceReservationsCurrent || 0)}
            compare={`${experiencePercentage}%`}
            icon={<icons.ExperienceIcon />}
            percentageNumber={experiencePercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Boutique"
            count={toLocaleString(data?.totalBoutiqueReservationsCurrent || 0)}
            compare={`${boutiquePercentage}%`}
            icon={<icons.BotiqueIcon />}
            percentageNumber={boutiquePercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Event"
            count={toLocaleString(data?.totalEventReservationsCurrent || 0)}
            compare={`${eventPercentage}%`}
            icon={<icons.EventIcon />}
            percentageNumber={eventPercentage}
          />
        </>
      ) : null}
    </BoxContainer>
  );
};

const BoxContainer = styled.div`
  margin-top: 20px;
  border: 1px solid #00000014;
  border-radius: 8px;
  padding: 30px;
`;
const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;
